import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { DynamicFormModel, DynamicFormService, DynamicInputModel, DynamicSelectModel } from '@ng-dynamic-forms/core';
import {
  CmsService,
  GlobalMessageService,
  GlobalMessageType,
  LanguageService,
  TranslatePipe,
  TranslationService,
  UrlPipe,
} from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { Observable, Subscription, combineLatest, forkJoin } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { CUSTOM_LAUNCH_CALLER } from 'src/app/dialog/custom-launch-dialog-config';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { CustomTranslationService } from 'src/app/service/language/custom-translation.service';
import { StandardModalService } from 'src/app/service/modal/standard-modal.service';
import { MyaccountB2bunitService, PostRequestDeletePayload } from 'src/app/service/myaccount/myaccount-b2bunit.service';
import { MyaccountCredentialsService } from 'src/app/service/myaccount/myaccount-credentials.service';
import {
  EDIT_DEALER_INFO,
  MyaccountFeaturesConfigService,
  REQUEST_DELETE_ACCOUNT,
} from 'src/app/service/myaccount/myaccount-features-config.service';
import { MyaccountInfoService } from 'src/app/service/myaccount/myaccount-info.service';
import { AddressWsDTO, MyaccountPersonalInfoService } from 'src/app/service/myaccount/myaccount-personal-info.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { GetStateUserPayload, RequestAccountService } from 'src/app/service/user/request-account.service';
import { DynamicFormsComponent } from '../../dynamic-forms/dynamic-forms.component';

@Component({
  selector: 'app-myaccount-personal-info',
  templateUrl: './myaccount-personal-info.component.html',
  styleUrls: ['./myaccount-personal-info.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [AsyncPipe],
  imports: [NgClass, MatIcon, DynamicFormsComponent, RouterLink, AsyncPipe, TranslatePipe, UrlPipe],
})
export class MyaccountPersonalInfoComponent implements OnInit, AfterContentChecked, OnDestroy {
  data$: Observable<any[]> = this.componentData.data$;
  personalInfo$: Observable<any>;
  hasPendingRequest: boolean;
  hasRequested = false;
  afterLoad = true;
  defaultValue = {};
  loading = true;

  currentPageSubscription$: Subscription;
  personalInfoFormGroup: FormGroup;
  personalInfoFormModel: DynamicFormModel;
  personalInfoFormModelSubscription$: Subscription;
  personalInfoFormGroupSubscription$: Subscription;
  mobilePrefix$: Observable<any>;

  firstNameInput: DynamicInputModel;
  lastNameInput: DynamicInputModel;
  emailInput: DynamicInputModel;
  contactNumInput: DynamicInputModel;
  countrySelect: DynamicSelectModel<string>;
  line1Input: DynamicInputModel;
  line2Input: DynamicInputModel;
  postalInput: DynamicInputModel;
  cityInput: DynamicInputModel;
  stateInput: DynamicSelectModel<string>;
  bottomScroll = false;
  errorMessages: any = [];
  isBtnDisabled = true;
  errorPersonalInfo = false;
  isRequestDeletionDisabled = true;
  isAdmin = false;
  stateData$: any;
  countryCodeIso: string;
  codeIso: string;
  count = 0;
  formFieldId: any = [];
  closeModalSubscription$: Subscription;

  postRequestDeletePayload: PostRequestDeletePayload = {
    fields: 'DEFAULT',
    userID: '',
  };

  activeCountry$: Observable<string> = this.countryService.getActive();

  isEditDealerInfoEnabled$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.myaccountFeaturesConfigSerivce.isMyaccountFeatureEnabled$(activeCountry, EDIT_DEALER_INFO)
    )
  );
  isRequestDeleteAccountEnabled$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.myaccountFeaturesConfigSerivce.isMyaccountFeatureEnabled$(activeCountry, REQUEST_DELETE_ACCOUNT)
    )
  );

  isCustomTranslationsEnabled$: Observable<boolean> = combineLatest([this.activeCountry$, this.activeLanguage$]).pipe(
    switchMap(([activeCountry, activeLanguage]) =>
      this.customTranslationService.isCustomTranslationsEnabled$(activeCountry, activeLanguage, [
        'personalInfo.hasPending',
      ])
    )
  );

  constructor(
    protected componentData: CmsComponentData<any>,
    private personalInfoSvc: MyaccountPersonalInfoService,
    private formService: DynamicFormService,
    private componentService: ComponentService,
    private cmsService: CmsService,
    private cdr: ChangeDetectorRef,
    private messageService: GlobalMessageService,
    private translation: TranslationService,
    private mycredentials: MyaccountCredentialsService,
    private myaccountInfo: MyaccountInfoService,
    private myAccountB2BUnitService: MyaccountB2bunitService,
    private requestAccountService: RequestAccountService,
    private languageService: LanguageService,
    private pipeAsync: AsyncPipe,
    private countryService: ShimanoCountryService,
    private customTranslationService: CustomTranslationService,
    private myaccountFeaturesConfigSerivce: MyaccountFeaturesConfigService,
    protected modalService: StandardModalService,
    protected el: ElementRef,
    protected vcr: ViewContainerRef,
    private storageService: StorageService
  ) {
    // DO NOT CHANGE THE ORDER
    this.formFieldId = [
      'firstName_full',
      'lastName_full',
      'emailAddress_full',
      'emailAddress_medium',
      'contactNumber',
      'contactNumberInvalid',
      'deliveryCountry',
      'addressLine1',
      'addressLine2',
      'postalCode',
      'city',
      'postalCode_valid',
    ];
  }

  ngOnInit() {
    this.data$.pipe(take(1)).subscribe((data) => data);

    this.currentPageSubscription$ = this.cmsService.getCurrentPage().subscribe((currentPage) => {
      if (currentPage && currentPage.pageId.includes('accountPersonalInfoPage')) {
        this.personalInfoFormModelSubscription$ = this.componentService.dynamicFormModel$.subscribe(
          (personalInfoFormModel) => {
            if (this.storageService.getSessionStorageItem('onbilling') !== 'true') {
              this.updateFormModel(personalInfoFormModel);
              this.getPersonalInfo();
            }
          }
        );
        this.personalInfoFormGroupSubscription$ = this.componentService.dynamicFormGroup$.subscribe(
          (personalInfoFormGroup) => {
            if (this.storageService.getSessionStorageItem('onbilling') !== 'true') {
              this.updateFormGroup(personalInfoFormGroup);
              this.isBtnDisabled = true;
            }
          }
        );
      }
    });

    // FOR REQUEST DELETION BUTTON
    const isAdminRequestDeletion = this.myaccountInfo.getMyAccountInfo();
    const isDisabledRequestDeletion = this.myAccountB2BUnitService.getB2BManagement();
    const userIdRequestDeletion = this.mycredentials.getYourCredentials();

    forkJoin([isAdminRequestDeletion, isDisabledRequestDeletion, userIdRequestDeletion])
      .pipe(take(1))
      .subscribe((res: any) => {
        const admin = res[0];
        const isDisable = res[1];
        const user = res[2];
        if (typeof admin.isB2BAdmin !== undefined) {
          this.isAdmin = admin.isB2BAdmin;
        }
        if (typeof isDisable.requestedForDeletion !== undefined) {
          this.isRequestDeletionDisabled = isDisable.requestedForDeletion;
        }
        if (typeof user !== undefined) {
          for (const key in user) {
            if (key === 'uid') {
              this.postRequestDeletePayload.userID = user[key];
              break;
            }
          }
        }
        this.cdr.detectChanges();
      });
  }

  onRequestDeletion() {
    this.storageService.removeSessionStorageItem('fieldChange');
    this.storageService.removeSessionStorageItem('user_input_values');

    this.modalService
      .openDialog(CUSTOM_LAUNCH_CALLER.REQUEST_DELETE_ACCOUNT, this.el, this.vcr, {
        payload: this.postRequestDeletePayload,
      })
      ?.pipe(take(1))
      .subscribe();

    this.closeModalSubscription$ = this.modalService.getClosedModalData().subscribe((result) => {
      if (result !== undefined) {
        this.isRequestDeletionDisabled = result.requestedForDeletion;
        this.cdr.detectChanges();
      }
    });
  }

  getPersonalInfo() {
    this.personalInfo$ = this.personalInfoSvc.getPersonalInfo();
    this.personalInfo$.pipe(take(1)).subscribe(
      (data) => {
        this.setPersonalInfo(data);
        this.loading = false;
      },
      (err) => {
        this.errorPersonalInfo = true;
        this.loading = false;
      }
    );
  }

  disableForm() {
    this.countrySelect.disabled = true;
    this.line1Input.disabled = true;
    this.line2Input.disabled = true;
    this.postalInput.disabled = true;
    this.cityInput.disabled = true;
    if (this.stateInput !== null) {
      this.stateInput.disabled = true;
    }
  }

  setValues(field: any, value: any) {
    if (typeof field !== 'undefined') {
      if (field && field.required) {
        if (value) {
          if (value === null && value === '') {
            this.isBtnDisabled = true;
          }
          return value;
        } else {
          this.isBtnDisabled = true;
        }
      } else {
        if (value) {
          return value;
        }
      }
      return '';
    }
  }

  setPersonalInfo(info: any) {
    this.loading = true;
    if (info && this.countrySelect) {
      this.countrySelect.value = this.setValues(this.countrySelect, info?.country?.isocode);
      this.countryCodeIso = info?.country?.isocode;
    }

    if (this.line1Input) {
      this.line1Input.value = this.setValues(this.line1Input, info?.line1);
    }
    if (info?.line2 && this.line2Input) {
      this.line2Input.value = this.setValues(this.line2Input, info?.line2);
    }
    if (this.postalInput) {
      this.postalInput.value = this.setValues(this.postalInput, info?.postalCode);
    }
    if (this.cityInput) {
      this.cityInput.value = this.setValues(this.cityInput, info?.town);
    }
    if (info?.region && this.stateInput) {
      this.stateInput.value = this.setValues(this.stateInput, info?.region?.isocode);
    }
    if (info?.approvalStatus === 'PENDING') {
      this.hasPendingRequest = true;
      this.disableForm();
    }
    if (this.personalInfoFormGroup) {
      this.defaultValue = Object.assign({}, this.personalInfoFormGroup.value);
    }
    this.loading = false;
  }

  updateFormModel(formModel: any) {
    this.personalInfoFormModel = formModel;
    this.countrySelect = this.formService.findModelById<DynamicSelectModel<string>>(
      'deliveryCountry',
      this.personalInfoFormModel
    );
    if (this.countrySelect !== null && this.countrySelect.required) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[6])),
      };
      this.countrySelect.required = true;
      this.countrySelect.errorMessages = hasError;
    }
    this.line1Input = this.formService.findModelById<DynamicInputModel>('addressLine1', this.personalInfoFormModel);
    if (this.line1Input !== null && this.line1Input.required) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[7])),
      };
      this.line1Input.required = true;
      this.line1Input.errorMessages = hasError;
    }
    if (this.line1Input !== null) {
      this.line1Input.maxLength = 255;
    }
    this.line2Input = this.formService.findModelById<DynamicInputModel>('addressLine2', this.personalInfoFormModel);
    if (this.line2Input !== null && this.line2Input.required) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[8])),
      };
      this.line2Input.required = true;
      this.line2Input.errorMessages = hasError;
    }
    if (this.line2Input !== null) {
      this.line2Input.maxLength = 255;
    }
    this.postalInput = this.formService.findModelById<DynamicInputModel>('postalCode', this.personalInfoFormModel);
    if (this.postalInput !== null && this.postalInput.required) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[9])),
        pattern: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[11])),
      };
      this.postalInput.required = true;
      this.postalInput.errorMessages = hasError;
    }
    this.cityInput = this.formService.findModelById<DynamicInputModel>('city', this.personalInfoFormModel);
    if (this.cityInput !== null && this.cityInput.required) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError(this.formFieldId[10])),
      };
      this.cityInput.required = true;
      this.cityInput.errorMessages = hasError;
    }
    this.stateInput = this.formService.findModelById<DynamicSelectModel<string>>('state', this.personalInfoFormModel);

    this.formService.detectChanges();
  }

  updateFormGroup(formGroup: any) {
    this.personalInfoFormGroup = formGroup;

    /* State */
    const optionStateValues: any = [];

    setTimeout(() => {
      if (
        this.personalInfoFormGroup.get('deliveryCountry') &&
        typeof this.personalInfoFormGroup.get('deliveryCountry') !== 'undefined'
      ) {
        this.countryCodeIso = this.personalInfoFormGroup.get('deliveryCountry').value;
      } else {
        this.countryCodeIso = null;
      }

      if (typeof this.countryCodeIso === 'undefined' || this.countryCodeIso === null) {
        this.count = 0;
        return;
      }

      if (this.countryCodeIso !== null || typeof this.countryCodeIso !== 'undefined') {
        const getStateUserPayload: GetStateUserPayload = {
          fields: 'DEFAULT',
          countryIsoCode: this.countryCodeIso,
        };
        this.stateData$ = this.requestAccountService.getStateForRequestAccount(getStateUserPayload);
        this.stateData$.pipe(take(1)).subscribe((data: any) => {
          if (data.items.length > 0) {
            this.count = data.items.length;
            for (const item of data.items) {
              const options = {
                label: item.name,
                value: item.code,
              };
              optionStateValues.push(options);
            }
          } else {
            this.count = 0;
          }

          if (this.stateInput !== null) {
            this.stateInput.options = optionStateValues;
            this.formService.detectChanges();
          }
        });
      }
    }, 1500);
    /* State */

    this.afterLoad = false;

    this.storageService.setSessionStorageItem('user_input_values', JSON.stringify(this.personalInfoFormGroup.value));
    this.isBtnDisabled = !this.personalInfoFormGroup.valid;
    this.personalInfoFormGroup.valueChanges.subscribe((data) => {
      if (!this.loading && data) {
        this.personalInfoFormGroup.markAsDirty();
        this.personalInfoFormGroup.markAllAsTouched();
      }
    });
    this.personalInfoFormGroup.updateValueAndValidity();
  }

  disablingButton(personalInfoFormGroup: FormGroup) {
    personalInfoFormGroup.valueChanges.subscribe((data) => {
      if (Object.keys(this.defaultValue).length > 0) {
        if (JSON.stringify(this.defaultValue).replace(/null/g, '""') === JSON.stringify(data).replace(/null/g, '""')) {
          this.isBtnDisabled = true;
        } else {
          this.isBtnDisabled = false;
        }
      }
    });
  }

  postPersonalInfo() {
    if (this.personalInfoFormGroup.valid) {
      const personalInfoDetails: AddressWsDTO = {
        country: this.countrySelect.value !== null ? { isocode: this.countrySelect.value.toString() } : { isocode: '' },
        line1: this.line1Input.value !== null ? this.line1Input.value.toString() : '',
        line2: this.line2Input.value !== null ? this.line2Input.value.toString() : '',
        postalCode: this.postalInput.value !== null ? this.postalInput.value.toString() : '',
        town: this.cityInput.value !== null ? this.cityInput.value.toString() : '',
      };

      if (this.stateInput !== null) {
        if (this.stateInput?.value) {
          personalInfoDetails.region = {
            countryIso: this.countrySelect.value.toString(),
            isocode: this.stateInput.value.toString(),
          };
        }
      }
      this.personalInfoSvc.postPersonalInfo(personalInfoDetails).subscribe((success) => {
        this.defaultValue = Object.assign({}, this.personalInfoFormGroup.value);
        this.isBtnDisabled = true;
        if (success.approvalStatus === 'PENDING') {
          this.messageService.add({ key: 'myAccount.successPersonalInfo' }, GlobalMessageType.MSG_TYPE_CONFIRMATION);
        }
        this.setPersonalInfo(success);
      });
    } else {
      Object.keys(this.personalInfoFormGroup.controls).forEach((key) => {
        const field = this.personalInfoFormGroup.controls[key];
        if ((field.value === '' || field.value === null) && !field.valid) {
          field.markAsDirty();
          field.markAsTouched();
          field.updateValueAndValidity();
        }
      });
      return;
    }
  }

  updatePersonalInfoModal() {
    this.modalService
      .openDialog(CUSTOM_LAUNCH_CALLER.CONFIRM_PERSONAL_INFO, this.el, this.vcr, {
        isValid: this.personalInfoFormGroup.valid,
      })
      ?.pipe(take(1))
      .subscribe();

    this.closeModalSubscription$ = this.modalService.getClosedModalData().subscribe((result) => {
      if (result !== undefined) {
        this.hasRequested = result.isTriggered;
        if (this.hasRequested) {
          this.postPersonalInfo();
          this.cdr.detectChanges();
        }
      }
    });
  }

  get activeLanguage$(): Observable<string> {
    return this.languageService.getActive();
  }

  getTranslatedError(id: string): Observable<string> {
    return this.translation.translate(`error.dynamicform.${id}`);
  }

  getFooterHeight(): number {
    const footer = document.getElementsByClassName('Footer')[0] as HTMLElement;
    if (this.bottomScroll || document.body.scrollHeight === window.innerHeight) {
      return footer.clientHeight;
    } else {
      return null;
    }
  }

  setCustomContext(isCustomTranslationsEnabled: boolean, activeCountry: string, activeLanguage: string) {
    return isCustomTranslationsEnabled ? [activeCountry, activeLanguage].join('-') : null;
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.closeModalSubscription$?.unsubscribe();

    if (this.currentPageSubscription$) {
      this.currentPageSubscription$.unsubscribe();
    }
    if (this.personalInfoFormModelSubscription$) {
      this.personalInfoFormModelSubscription$.unsubscribe();
    }
    if (this.personalInfoFormGroupSubscription$) {
      this.personalInfoFormGroupSubscription$.unsubscribe();
    }
  }
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.bottomScroll = false;
    if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 1) {
      this.bottomScroll = true;
    }
    this.cdr.detectChanges();
  }
}
